<script lang="ts">
  import { onDestroy } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import { push } from 'svelte-spa-router';

  Chart.register(...registerables);

  let { data = [], viewMode = 'status' } = $props<{
    data?: any[];
    viewMode?: 'status' | 'models';
  }>();

  let canvasEl: HTMLCanvasElement;
  let chart: Chart | null = null;

  const MODEL_CONFIG: Record<string, { label: string; color: string; hoverColor: string }> = {
    isolation_forest: {
      label: 'Isolation Forest Outliers',
      color: '#6366f1',
      hoverColor: '#818cf8',
    },
    spike_detection_rule: {
      label: 'Burst & Surge Spikes',
      color: '#f59e0b',
      hoverColor: '#fbbf24',
    },
    night_flow_rule: {
      label: 'Night Micro-Leak Creep',
      color: '#f43f5e',
      hoverColor: '#fb7185',
    },
    zero_flow_rule: {
      label: 'Zero Flow Obstructions',
      color: '#06b6d4',
      hoverColor: '#22d3ee',
    },
    acoustic_classifier: {
      label: 'Acoustic Fixture Signatures',
      color: '#10b981',
      hoverColor: '#34d399',
    },
    general_heuristic: {
      label: 'General ML Heuristics',
      color: '#8b5cf6',
      hoverColor: '#a78bfa',
    },
  };

  function getDatasets(isDark: boolean) {
    if (viewMode === 'models') {
      // Find all models across items
      const modelKeysSet = new Set<string>();
      for (const d of data) {
        if (d.model_counts) {
          for (const k of Object.keys(d.model_counts)) {
            if (d.model_counts[k] > 0) {
              modelKeysSet.add(k);
            }
          }
        }
      }
      const modelKeys = Array.from(modelKeysSet);
      if (modelKeys.length > 0) {
        return modelKeys.map(k => {
          const cfg = MODEL_CONFIG[k] || {
            label: k.replace(/_/g, ' '),
            color: '#a855f7',
            hoverColor: '#c084fc',
          };
          return {
            label: cfg.label,
            data: data.map((d: any) => d.model_counts?.[k] || 0),
            backgroundColor: cfg.color,
            hoverBackgroundColor: cfg.hoverColor,
            borderRadius: 4,
          };
        });
      }
    }

    // Default status mode: New vs Resolved
    const primaryColor = isDark ? '#818cf8' : '#4f46e5';
    const successColor = isDark ? '#34d399' : '#10b981';

    return [
      {
        label: 'New & Investigating',
        data: data.map((d: any) => d.new_count || 0),
        backgroundColor: primaryColor,
        hoverBackgroundColor: isDark ? '#a5b4fc' : '#6366f1',
        borderRadius: 4,
      },
      {
        label: 'Resolved & False Positive',
        data: data.map((d: any) => d.resolved_count || 0),
        backgroundColor: successColor,
        hoverBackgroundColor: isDark ? '#6ee7b7' : '#34d399',
        borderRadius: 4,
      }
    ];
  }

  function buildChart() {
    if (!canvasEl || data.length === 0) {
      if (chart) { chart.destroy(); chart = null; }
      return;
    }

    const labels = data.map((d: any) => new Date(d.date).toLocaleDateString([], { month: 'short', day: 'numeric' }));
    const isDark = document.documentElement.classList.contains('dark');
    const datasets = getDatasets(isDark);

    if (chart) {
      chart.destroy();
      chart = null;
    }

    chart = new Chart(canvasEl, {
      type: 'bar',
      data: {
        labels,
        datasets,
      },
      options: {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        onHover: (event, elements) => {
          const canvas = event.native?.target as HTMLCanvasElement;
          if (canvas) {
            canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
          }
        },
        onClick: (_event, elements) => {
          if (elements.length > 0) {
            const idx = elements[0].index;
            const dateStr = data[idx]?.date;
            if (dateStr) {
              push(`/admin/alerts?tab=history&date=${dateStr}`);
            }
          }
        },
        plugins: {
          legend: {
            position: 'top',
            labels: {
              usePointStyle: true,
              boxWidth: 8,
              font: { family: 'Inter', size: 11 },
            }
          },
          tooltip: {
            titleFont: { size: 13, family: 'Inter', weight: 'bold' },
            bodyFont: { size: 12, family: 'Inter' },
            footerFont: { size: 10, family: 'Inter', style: 'italic' },
            padding: 12,
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              footer: () => 'Click to inspect in Alerts History'
            }
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: {
              font: { size: 10, family: 'Inter' },
            },
            border: { display: false },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              color: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)',
            },
            ticks: {
              font: { size: 11, family: 'Inter' },
              precision: 0,
            },
            border: { display: false },
            title: {
              display: true,
              text: 'Detections',
              font: { size: 11, family: 'Inter' },
            }
          },
        },
      },
    });
  }

  $effect(() => {
    if (data && canvasEl) {
      // Re-trigger on data or viewMode changes
      void viewMode;
      buildChart();
    }
  });

  onDestroy(() => {
    if (chart) chart.destroy();
  });
</script>

<div class="h-64 w-full">
  <canvas bind:this={canvasEl}></canvas>
</div>
